'use client';

import { useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Check, ChevronDown, ChevronUp, Crown } from 'lucide-react';
import { LegalDocumentPage } from '@/features/support/ui/LegalDocumentPage';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { Button } from '@/shared/ui/shadcn/button';
import { LoadingSpinner } from '@/shared/ui/LoadingSpinner';
import { usePasses } from '@/shared/api/hooks/use-passes';
import {
  usePassPaywallStore,
  type PassPaywallHighlight,
} from '@/shared/stores/pass-paywall-store';
import { cn } from '@/shared/lib/cn';
import type { Pass, PassPaywallCopy } from '@/types/passes';

/**
 * Map a paywall highlight to the Pass `entitlement_key`. These keys are the
 * server's stable identifiers (mirrors Apple's `Highlight` rawValues:
 * pro → pro_pass, designer → designer_pass). The opaque keys are never
 * composed on the client beyond this lookup.
 */
const ENTITLEMENT_KEY: Record<PassPaywallHighlight, string> = {
  pro: 'pro_pass',
  designer: 'designer_pass',
};

/**
 * Root-mounted Pass paywall — the monetization-pivot upsell for locked
 * Pro/Designer render-settings features. Mirrors Apple's `PassPaywallSheet`:
 * hero placeholder, server tagline/subtitle/feature checklist, single primary
 * CTA, the *other* Pass behind an expandable disclosure (pre-expanded when the
 * paywall opens highlighting Designer), restore + terms footer.
 *
 * All display copy is server-driven via `GET /v1/passes` (principle 5) — the
 * client never composes Pass descriptions locally. Purchase is out of scope
 * (Stripe Checkout = pivot tasks D1/D2): the CTA is an inert placeholder, never
 * fabricating a purchase or entitlement (money path: no fallbacks/fakes).
 */
export function PassPaywallDialog() {
  const t = useTranslations('passes');
  const tCommon = useTranslations('common');

  const highlight = usePassPaywallStore((s) => s.highlight);
  const close = usePassPaywallStore((s) => s.close);

  // Gate the (auth-only) catalog fetch on the dialog actually being open — the
  // dialog is root-mounted, so an ungated query would 401 on pre-auth pages.
  const { data: passes = [], isLoading, isError, refetch } = usePasses({
    enabled: highlight !== null,
  });

  // Disclosure (the non-highlighted Pass) opens pre-expanded when the paywall
  // was triggered by a Designer-exclusive feature, so the user immediately sees
  // the Pass that actually unlocks what they tapped — mirrors Apple's
  // `_isDesignerExpanded = State(initialValue: highlight == .designer)`.
  const [isDisclosureExpanded, setIsDisclosureExpanded] = useState(false);
  useEffect(() => {
    if (highlight) setIsDisclosureExpanded(highlight === 'designer');
  }, [highlight]);

  const primaryPass = useMemo<Pass | undefined>(() => {
    if (!highlight) return undefined;
    return passes.find(
      (p) => p.attributes.entitlement_key === ENTITLEMENT_KEY[highlight]
    );
  }, [passes, highlight]);

  const disclosurePass = useMemo<Pass | undefined>(() => {
    if (!highlight) return undefined;
    const other: PassPaywallHighlight = highlight === 'pro' ? 'designer' : 'pro';
    return passes.find(
      (p) => p.attributes.entitlement_key === ENTITLEMENT_KEY[other]
    );
  }, [passes, highlight]);

  return (
    <Dialog
      open={highlight !== null}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogContent
        showCloseButton
        className="max-w-[460px] max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{t('sectionTitle')}</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="py-20">
            <LoadingSpinner size="lg" />
          </div>
        ) : isError || !primaryPass ? (
          <div className="flex flex-col items-center gap-4 py-16 text-center">
            <p className="text-card-body text-muted-foreground">
              {t('loadError')}
            </p>
            <Button variant="outline" onClick={() => refetch()}>
              {tCommon('tryAgain')}
            </Button>
          </div>
        ) : (
          <PaywallBody
            primary={primaryPass}
            disclosure={disclosurePass}
            isDisclosureExpanded={isDisclosureExpanded}
            onToggleDisclosure={() => setIsDisclosureExpanded((v) => !v)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function PaywallBody({
  primary,
  disclosure,
  isDisclosureExpanded,
  onToggleDisclosure,
}: {
  primary: Pass;
  disclosure: Pass | undefined;
  isDisclosureExpanded: boolean;
  onToggleDisclosure: () => void;
}) {
  const t = useTranslations('passes');
  const { paywall, is_active } = primary.attributes;
  // In-app Terms — shown in a nested dialog OVER the paywall, never a web
  // hand-off (keeps the user next to the CTA). Reuses the same LegalDocumentPage
  // the Account screen renders.
  const [showTerms, setShowTerms] = useState(false);

  return (
    <div className="flex flex-col items-center gap-6">
      {/* Owned → celebration (the buy already happened — entitlements follow the
          user_id, so a Pass bought on mobile shows owned here too); not owned →
          the purchase funnel. */}
      {is_active ? (
        <OwnedCelebration
          paywall={paywall}
          statusActiveLabel={t('statusActive')}
          isTopTier={primary.attributes.entitlement_key === ENTITLEMENT_KEY.designer}
        />
      ) : (
        <>
          {/* Hero headline ABOVE the offer card. Per-pass server copy ("Render
              like a pro — forever" for Pro, parallel for Designer) — carries
              the lifetime promise where it gets the most attention, before any
              feature bullet. Centered to balance the card below. Mirrors
              Apple / Android. */}
          <h2 className="text-card-title text-foreground w-full text-center font-bold">
            {paywall.headline}
          </h2>

          {/* Plastic-card design: bold tagline anchored top-left, "Lifetime
              access" small on the top-right (constrained width forces a 2-line
              wrap, left-flagged) — like a brand mark + tier label on a real
              credit/membership card. Tier-accent ring (teal for Pro, gold for
              Designer) + faint tint carry the Pass identity; a thin neutral
              shadow-md below gives the card "thickness". The tier-tinted halo
              (shadow-{tier}/25) was dropped — cleaner surface, accent stays
              only in the border/tint. */}
          <div
            className={cn(
              'flex w-full flex-col gap-4 rounded-xl p-4 shadow-md ring-1',
              primary.attributes.entitlement_key === ENTITLEMENT_KEY.designer
                ? 'bg-gold/5 ring-gold/40'
                : 'bg-primary/5 ring-primary/40'
            )}
          >
            <div className="flex w-full items-start gap-4">
              {/* `shrink-0 whitespace-nowrap` so the PRODUCT NAME ("Designer
                  Pass") always shows in full and never wraps/truncates — on a
                  narrow viewport the long price_note used to steal the row, and
                  an ellipsized product name risks an App Store rejection (the
                  purchased product must be fully named). The note yields
                  instead (see below). Mirrors Apple `layoutPriority` / Android
                  measure-first. */}
              <h2 className="shrink-0 whitespace-nowrap text-card-title text-foreground font-bold">
                {paywall.tagline}
              </h2>
              {/* priceNote pinned to the top-right (`ml-auto`). `text-foreground`
                  (black) so it reads against the faint tier-tinted background —
                  `text-muted-foreground` was too washed out at this small size.
                  `truncate` yields to the product name if space is tight. */}
              <p className="ml-auto min-w-0 truncate text-card-meta font-semibold text-foreground">
                {paywall.price_note}
              </p>
            </div>

            <FeaturesList features={paywall.features} />
          </div>

          {/* Primary CTA. Purchase is out of scope (Stripe Checkout = D1/D2), so
              the button is an inert placeholder — disabled so it never implies a
              working purchase. We must NOT fabricate a purchase/entitlement on
              the money path. */}
          <div className="flex w-full flex-col items-center gap-2">
            <Button
              className="w-full shadow-none hover:shadow-none"
              size="lg"
              disabled
              aria-disabled
            >
              {/* cta_label is server-built ("Unlock :name — :price") and already
                  includes the price — render it verbatim. Mirrors Apple/Android;
                  do NOT append the price again (that double-printed it). */}
              {paywall.cta_label}
            </Button>
            {/* Under-CTA "Lifetime access" was removed — the lifetime promise now
                lives once at the top of the offer card (price_note replacing
                the old billing_note position). One clear statement reads
                stronger than two. */}
          </div>
        </>
      )}

      {/* Skip the disclosure entirely when the OTHER Pass is already owned:
          offering an expandable "buy" row for a Pass the user holds (e.g. Pro
          shown under Designer when Pro is active) is pointless and reads as
          confusing dead UI. Only surface it as a genuine upsell. */}
      {disclosure && !disclosure.attributes.is_active && (
        <DisclosureRow
          pass={disclosure}
          // The toggle prompt is the PRIMARY Pass's invitation to the higher
          // tier ("Are you a designer?"), not the disclosed Pass's own label
          // (Designer is the top tier and has none). Fall back to its name.
          toggleLabel={
            paywall.cta_disclosure_label ?? disclosure.attributes.name
          }
          isExpanded={isDisclosureExpanded}
          onToggle={onToggleDisclosure}
        />
      )}

      {/* Expectation-setting fine print — a Pass unlocks features/limits,
          generation still spends tokens. `text-muted-foreground` matches the
          surrounding grey labels (Restore · Terms below) so it reads at the
          same contrast — the previous `/70` opacity was too washed-out to
          actually be read. */}
      <p className="text-center text-card-meta text-muted-foreground">
        {paywall.token_note}
      </p>

      {/* Restore + Terms footer — visual mirror of Apple. On web "restore" =
          re-login (entitlements follow user_id), so per pivot task D5 there is no
          active StoreKit restore here; the label is inert chrome. Terms opens the
          in-app legal document in a nested dialog over the paywall. */}
      <div className="flex items-center gap-3 text-card-meta text-muted-foreground">
        <span>{paywall.restore_label}</span>
        <span className="text-foreground/30">·</span>
        <button
          type="button"
          onClick={() => setShowTerms(true)}
          className="transition-colors hover:text-foreground"
        >
          {paywall.terms_label}
        </button>
      </div>

      <Dialog open={showTerms} onOpenChange={setShowTerms}>
        <DialogContent
          showCloseButton
          className="max-w-2xl max-h-[85vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle>{paywall.terms_label}</DialogTitle>
          </DialogHeader>
          <LegalDocumentPage type="terms" />
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Shared feature checklist used by both the purchase funnel and the owned
 *  celebration. */
function FeaturesList({ features }: { features: string[] }) {
  return (
    <ul className="flex w-full flex-col gap-2">
      {features.map((feature) => (
        <li key={feature} className="flex items-start gap-2">
          <Check
            className="mt-0.5 h-4 w-4 shrink-0 text-primary"
            strokeWidth={2.5}
          />
          <span className="text-card-body text-foreground">{feature}</span>
        </li>
      ))}
    </ul>
  );
}

/** Post-purchase celebration shown once the Pass is owned. Mirrors the
 *  owned-Pass card on the Tokens page (`PassRow`): a crown, a glowing teal frame
 *  + faint tint, and the filled "Active" capsule — so the owner feels the
 *  upgrade actually landed instead of reading a greyed-out "Active" button. Copy
 *  is server-driven (`owned_headline` / `owned_note`); the badge label stays
 *  i18n-owned (`statusActive`), matching the Tokens page. */
function OwnedCelebration({
  paywall,
  statusActiveLabel,
  isTopTier,
}: {
  paywall: PassPaywallCopy;
  statusActiveLabel: string;
  isTopTier: boolean;
}) {
  // Premium top tier (Designer Pass) is celebrated in gold; lower tiers (Pro
  // Pass) keep the brand teal. Full static class strings so Tailwind keeps them.
  // Same plastic-card chrome as the purchase hero — accent ring + tint only,
  // thin neutral shadow-md below for thickness, no tier-tinted halo.
  const frameClass = isTopTier
    ? 'flex w-full flex-col items-center gap-3 rounded-xl bg-gold/5 p-6 text-center shadow-md ring-1 ring-gold/40'
    : 'flex w-full flex-col items-center gap-3 rounded-xl bg-primary/5 p-6 text-center shadow-md ring-1 ring-primary/40';
  const crownClass = isTopTier ? 'h-10 w-10 text-gold' : 'h-10 w-10 text-primary';
  const badgeClass = isTopTier
    ? 'text-card-meta text-gold inline-flex items-center gap-1 rounded-full bg-gold/10 px-2.5 py-1'
    : 'text-card-meta text-primary inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1';
  return (
    <div className={frameClass}>
      <Crown className={crownClass} />
      <h2 className="text-card-title text-foreground">{paywall.owned_headline}</h2>
      <span className={badgeClass}>
        <Check className="h-3.5 w-3.5" />
        {statusActiveLabel}
      </span>
      <p className="text-card-body text-muted-foreground">{paywall.owned_note}</p>
      <FeaturesList features={paywall.features} />
    </div>
  );
}

/** The non-highlighted Pass, collapsed under an expandable row. Mirrors Apple's
 *  `disclosureRow`: tappable header + chevron, expanded body with tagline,
 *  feature checklist, and an inert secondary CTA. */
function DisclosureRow({
  pass,
  toggleLabel,
  isExpanded,
  onToggle,
}: {
  pass: Pass;
  toggleLabel: string;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const { paywall } = pass.attributes;

  return (
    // Hairline outline (no fill, no shadow) groups the toggle + chevron so they
    // don't drift to opposite edges with nothing connecting them, while staying
    // far quieter than the old filled box. Mirrors Apple/Android.
    <div className="w-full rounded-xl border border-border p-4">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between"
        aria-expanded={isExpanded}
      >
        {/* Subtle: muted + small so the optional Designer upsell doesn't
            compete with the primary purchase CTA. Mirrors Apple/Android. */}
        <span className="text-card-meta text-muted-foreground">
          {toggleLabel}
        </span>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>

      <div
        className={cn(
          'overflow-hidden transition-all',
          isExpanded ? 'mt-3 max-h-[600px]' : 'max-h-0'
        )}
      >
        <p className="text-card-body text-foreground">{paywall.tagline}</p>
        <ul className="mt-2 flex flex-col gap-1.5">
          {paywall.features.map((feature) => (
            <li key={feature} className="flex items-start gap-2">
              <Check
                className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary"
                strokeWidth={2.5}
              />
              <span className="text-card-body text-foreground">{feature}</span>
            </li>
          ))}
        </ul>
        {/* Inert secondary CTA — same out-of-scope purchase placeholder as the
            primary; disabled so it never fakes a purchase. */}
        <Button variant="outline" className="mt-3 w-full" disabled aria-disabled>
          {/* cta_label already includes the price — render verbatim, no append. */}
          {paywall.cta_label}
        </Button>
        {/* Under-CTA "Lifetime access" was removed from the disclosure row too
            (no lifetime line under disclosure CTA on Apple/Android either). The
            promise still reads at the top of the primary card above. */}
      </div>
    </div>
  );
}
